// frontend/src/components/shared/Header.tsx

import { LogOut, Menu, Search, Settings, User as UserIcon } from 'lucide-react';
import { type KeyboardEvent, useState } from 'react';
import { Link, NavLink, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Separator } from '@/components/ui/separator';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';
import { Skeleton } from '@/components/ui/skeleton';
import { useApp } from '@/context/AppContext';
import { useAuth } from '@/context/AuthContext';
import { usePermissions } from '@/hooks/usePermissions';
import { useSmartSkeleton } from '@/hooks/useSmartSkeleton';
import { cn } from '@/lib/utils';
import { AppLogo } from './AppLogo';
import { ModeToggle } from './mode-toggle';
import { NavBadgeDisplay } from './NavBadgeDisplay';

interface HeaderProps {
  onCommandOpen?: () => void;
}

export function Header({ onCommandOpen }: HeaderProps = {}) {
  const app = useApp();
  const { user, logout, isLoading } = useAuth();
  const { checkPermission } = usePermissions();
  const navigate = useNavigate();
  const [isMobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const showUserSkeleton = useSmartSkeleton(isLoading);

  const handleLogout = async () => {
    await logout();
    navigate('/login?reason=logout');
    setMobileMenuOpen(false);
  };

  const handleSearchKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      const q = searchQuery.trim();
      navigate(q ? `/search?q=${encodeURIComponent(q)}` : '/search');
      setSearchQuery('');
    }
  };

  const hasNavItemAccess = (navItem: any) => {
    if (!navItem.requiredPermissions || navItem.requiredPermissions.length === 0) {
      return true;
    }
    return navItem.requiredPermissions.every((permission: any) => checkPermission(permission));
  };

  // Define the link classes once. This is what NavLink expects.
  const navLinkClasses = ({ isActive }: { isActive: boolean }) =>
    cn(
      'flex items-center gap-3 rounded-lg px-3 py-2 text-muted-foreground transition-colors hover:text-primary',
      isActive && 'bg-muted text-primary'
    );

  return (
    <header className="flex h-14 items-center gap-4 border-b bg-muted/40 px-4 lg:h-[60px] lg:px-6">
      <Sheet open={isMobileMenuOpen} onOpenChange={setMobileMenuOpen}>
        <SheetTrigger asChild>
          <Button variant="outline" size="icon" className="shrink-0 md:hidden">
            <Menu className="h-5 w-5" />
            <span className="sr-only">Toggle navigation menu</span>
          </Button>
        </SheetTrigger>
        <SheetContent side="left" className="flex flex-col w-full sm:w-3/4">
          <NavLink
            to="/"
            className="flex h-14 items-center border-b px-4 pr-12 text-lg font-semibold"
            onClick={() => setMobileMenuOpen(false)}
          >
            <AppLogo className="h-6 w-6 text-primary" />
            <span className="ml-2">{app.name}</span>
          </NavLink>

          <nav className="flex-1 overflow-y-auto grid items-start gap-1 p-4 text-sm font-medium">
            {app.navItems
              .filter((section) => section.links.some(hasNavItemAccess))
              .map((section, sectionIndex) => (
                <div key={section.title || sectionIndex} className="py-2">
                  {section.title && (
                    <h3 className="mb-1 rounded-lg px-3 text-xs font-semibold uppercase text-muted-foreground">
                      {section.title}
                    </h3>
                  )}
                  {section.links.filter(hasNavItemAccess).map((link) => (
                    <NavLink
                      key={link.href}
                      to={link.href}
                      className={navLinkClasses}
                      end={link.end}
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      <link.icon className="h-4 w-4" />
                      {link.label}
                      {link.badge && <NavBadgeDisplay badge={link.badge} />}
                    </NavLink>
                  ))}
                </div>
              ))}
          </nav>

          <div className="mt-auto flex flex-col p-4">
            <Separator className="mb-4" />
            <div className="flex flex-col gap-1">
              {showUserSkeleton ? (
                <div className="flex items-center gap-3 px-3 py-1">
                  <Skeleton className="h-8 w-8 rounded-full" />
                  <div className="flex flex-col gap-1 min-w-0 flex-1">
                    <Skeleton className="h-4 w-20" />
                    <Skeleton className="h-3 w-32" />
                  </div>
                </div>
              ) : (
                <div className="flex items-center gap-3 px-3 py-1">
                  <div className="grid h-8 w-8 place-items-center rounded-full bg-muted-foreground/20">
                    <UserIcon className="h-5 w-5 text-muted-foreground" />
                  </div>
                  <div className="flex flex-col min-w-0">
                    <span className="font-semibold truncate">{user?.name}</span>
                    <span className="text-xs text-muted-foreground truncate">{user?.email}</span>
                  </div>
                </div>
              )}
              <nav className="grid items-start text-sm font-medium">
                <Link
                  to="/profile"
                  className={cn(navLinkClasses({ isActive: false }), 'w-full py-1.5')}
                  onClick={() => setMobileMenuOpen(false)}
                >
                  <Settings className="h-4 w-4" />
                  Account Settings
                </Link>
                <Button
                  variant="ghost"
                  onClick={handleLogout}
                  className={cn(
                    navLinkClasses({ isActive: false }),
                    'w-full justify-start py-1.5 text-destructive hover:text-destructive'
                  )}
                >
                  <LogOut className="h-4 w-4" />
                  Logout
                </Button>
              </nav>
            </div>
          </div>
        </SheetContent>
      </Sheet>

      {/* DMS: document search bar + Cmd+K hint */}
      {app.searchEnabled && (
        <div className="relative w-full max-w-md flex-1">
          <Search className="text-muted-foreground absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2" />
          <Input
            placeholder={app.searchPlaceholder || 'Search...'}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyDown={handleSearchKeyDown}
            className="pl-9 pr-14"
          />
          {onCommandOpen && (
            <button
              type="button"
              onClick={onCommandOpen}
              className="absolute right-2 top-1/2 -translate-y-1/2 inline-flex h-5 select-none items-center rounded border bg-muted px-1.5 text-[11px] leading-none tracking-wide text-muted-foreground hover:text-foreground hover:bg-muted/80 transition-colors"
              title="Command palette (⌘K)"
            >
              ⌘K
            </button>
          )}
        </div>
      )}

      {/* Platform (or any app without its own search): Cmd+K button */}
      {!app.searchEnabled && onCommandOpen && (
        <Button
          variant="outline"
          className="relative h-9 w-full max-w-sm flex-1 justify-start text-sm text-muted-foreground"
          onClick={onCommandOpen}
        >
          <Search className="mr-2 h-4 w-4" />
          Search...
          <kbd className="ml-auto pointer-events-none inline-flex h-5 select-none items-center gap-1 rounded border bg-muted px-1.5 font-mono text-[10px] font-medium">
            <span className="text-xs">⌘</span>K
          </kbd>
        </Button>
      )}

      <div className="ml-auto flex items-center gap-1">
        <ModeToggle />
      </div>
    </header>
  );
}
